import { useState, useEffect, useRef, useCallback } from 'react';
import './CashFlow.scss';
import CashFlowHeader from './CashFlowHeader';
import CashFlowRow from './CashFlowRow';
import CashFlowSummary from './CashFlowSummary';
import { CashFlowCategory } from './utils/categoryUtils';
import { ArrowLeft, ArrowRight, ColumnTripleIcon, PendingLockSvg } from '../../modules/default-svg';
import { BarChart2, TableProperties, Pencil, Eye } from 'lucide-react';
import projectService from "../../store/services/projects.service";
import userService from "../../store/services/user.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import Skeleton from 'react-loading-skeleton';
import CustomPopover from '../../components/popover/Popover';
import Button from '../../components/controls/button';
import CheckboxInputStateSave from '../../components/controls/checkboxStateSave/CheckboxStateSave';
import { CASHFLOW_COLUMNS } from '../../helpers/columns';
import { debounce } from 'lodash';
import ExportButton from '../../components/Export/ExportButton';
import { getProjectBudgetSummary } from '../../store/actions/projects.actions';
import { useColumnWidths } from './useColumnWidths';
import CashFlowCharts from './CashFlowCharts';
import CashFlowKPIBar from './CashFlowKPIBar';
import KpiGaugeSection from './KpiGaugeSection';
import { Tooltip } from '@fluentui/react-components';
import { getEditPermission } from '../../helpers/utils';

const CashFlow = (props) => {
  const { projectId, isHeaderCollapsed } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [cashFlow, setCashFlow] = useState(null);
  const [categories, setCategories] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const tableWrapperRef = useRef(null);
  const dispatch = useDispatch();
  const settings = JSON.parse(localStorage.getItem("settings"));
  const layoutName = "Project-Cashflow";
  const defaultLayout = CASHFLOW_COLUMNS.map((c) => ({
    name: c.label,
    order: c.order,
    isVisible: c.isVisible,
    customName: ""
  }));
  const [columns, setColumns] = useState(defaultLayout);
  const [data, setData] = useState({
    monthly: [],
    yearly: {
      totalInv: 0,
      totalAc: 0,
      totalGp: 0,
      totalGpPercentage: 0
    }
  });

  const { columnWidths, startResize, getWidth, resetWidths } = useColumnWidths();

  const regionalSettings = {
    currencySymbol: settings?.regionalSettings?.currency?.split(" ")[1] || '$',
    groupSeparator: settings?.regionalSettings?.groupSeparator || ',',
    decimalSeparator: settings?.regionalSettings?.decimalSeparator || '.'
  };

  const [kpis, setKpis] = useState(null);
  const [isKpiSyncing, setIsKpiSyncing] = useState(false);
  const fetchKpisRef = useRef(null);
  const fetchFinancialKpis = () => {
    if (!projectId) return;
    setIsKpiSyncing(true);
    projectService.getProjectFinancialKpisRequest(projectId)
      .then(data => setKpis(data))
      .catch(() => { })
      .finally(() => setIsKpiSyncing(false));
  };
  fetchKpisRef.current = fetchFinancialKpis;

  const recalculateMonthlyTotals = useCallback((monthData) => {
    const newMonthlyData = monthData.map(month => {
      const monthLines = month.lines || [];
      // Only include top-level lines (no parentLineId) to avoid double-counting sub-lines
      const topLevelLines = monthLines.filter(line => !line.parentLineId);
      const totalInv = topLevelLines.reduce((sum, line) => sum + (line.inv || 0), 0);
      const totalAc = topLevelLines.reduce((sum, line) => sum + (line.ac || 0), 0);
      const totalGp = totalInv - totalAc;
      const totalGpPercentage = totalInv !== 0 ? (totalGp / totalInv) * 100 : 0;
      const totalCv = topLevelLines.reduce((sum, line) => sum + (line.cv || 0), 0);
      const totalActualHours = topLevelLines.reduce((sum, line) => sum + (line.actualHours || 0), 0);
      const totalForecastHv = topLevelLines.reduce((sum, line) => sum + (line.ForecastHv || 0), 0);
      return { ...month, totalInv, totalAc, totalGp, totalGpPercentage, totalCv, totalActualHours, totalForecastHv };
    });

    const yearlyTotalInv = newMonthlyData.reduce((sum, m) => sum + m.totalInv, 0);
    const yearlyTotalAc = newMonthlyData.reduce((sum, m) => sum + m.totalAc, 0);
    const yearlyTotalGp = yearlyTotalInv - yearlyTotalAc;
    const yearlyTotalGpPercentage = yearlyTotalInv > 0 ? (yearlyTotalGp / yearlyTotalInv) * 100 : 0;
    const yearlyTotalCv = newMonthlyData.reduce((sum, m) => sum + m.totalCv, 0);

    const allLines = new Map();
    newMonthlyData.forEach(month => {
      const monthLinesList = month.lines || [];
      // Exclude parent lines — sum directly from standalone lines + sub-lines to avoid
      // decimal loss from the parent's rate-based re-aggregation
      const parentIdSet = new Set(monthLinesList.filter(l => l.parentLineId).map(l => l.parentLineId));
      monthLinesList.filter(l => !parentIdSet.has(l.id)).forEach(line => {
        if (!allLines.has(line.id)) allLines.set(line.id, []);
        allLines.get(line.id).push(line);
      });
    });

    let yearlyForecastAllocationSum = 0;
    let yearlyForecastHours = 0;
    let yearlyForecastTotalCost = 0;
    let yearlyForecastTotalBill = 0;
    let yearlyActualHours = 0;
    let yearlyForecastHv = 0;

    allLines.forEach((months) => {
      const nonZeroMonths = months.filter(m => (m.forecastAllocation || 0) !== 0);
      const lineAvgAllocation = nonZeroMonths.length > 0
        ? nonZeroMonths.reduce((s, m) => s + m.forecastAllocation, 0) / nonZeroMonths.length : 0;

      yearlyForecastAllocationSum += lineAvgAllocation;
      yearlyForecastHours += months.reduce((s, m) => s + (m.forecastHours || 0), 0);
      yearlyForecastTotalCost += months.reduce((s, m) => s + (m.forecastTotalCost || 0), 0);
      yearlyForecastTotalBill += months.reduce((s, m) => s + (m.forecastTotalBill || 0), 0);
      yearlyActualHours += months.reduce((s, m) => s + (m.actualHours || 0), 0);
      yearlyForecastHv += months.reduce((s, m) => s + (m.ForecastHv || 0), 0);
    });

    return {
      monthly: newMonthlyData,
      yearly: {
        totalInv: yearlyTotalInv, totalAc: yearlyTotalAc, totalGp: yearlyTotalGp,
        totalGpPercentage: yearlyTotalGpPercentage, totalCv: yearlyTotalCv,
        avgForecastAllocation: yearlyForecastAllocationSum,
        totalForecastHours: yearlyForecastHours,
        avgForecastHourlyCostRate: yearlyForecastHours > 0 ? yearlyForecastTotalCost / yearlyForecastHours : 0,
        avgForecastHourlyBillRate: yearlyForecastHours > 0 ? yearlyForecastTotalBill / yearlyForecastHours : 0,
        totalForecastTotalCost: yearlyForecastTotalCost,
        totalForecastTotalBill: yearlyForecastTotalBill,
        totalActualHours: yearlyActualHours,
        totalForecastHv: yearlyForecastHv,
      }
    };
  }, []);

  const handleCashfLowData = useCallback((cashFlow) => {
    const allCategories = Object.values(CashFlowCategory).filter(cat => typeof cat === 'number');
    const usedCategories = [...new Set(cashFlow.lines.map(line => line.category))].filter(Boolean);
    const categories = [...new Set([...allCategories, ...usedCategories])].sort((a, b) => a - b);
    setCategories(categories);
    setYear(cashFlow.year);

    const lineItemsMap = new Map();
    cashFlow.lines.forEach(line => {
      line.items.forEach(item => {
        const key = `${line.id}-${item.month}`;
        lineItemsMap.set(key, item);
      });
    });

    const monthlyData = Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const monthLines = cashFlow.lines.map(line => {
        const key = `${line.id}-${month}`;
        const monthItem = lineItemsMap.get(key);

        const forecastAllocation = monthItem?.forecastAllocation || 0;
        const forecastHours = monthItem?.forecastHours || 0;
        const forecastHourlyCostRate = monthItem
          ? (monthItem.forecastHourlyCostRate || 0)
          : (line.linkedResource?.hourlyRate || 0);
        const forecastHourlyBillRate = monthItem
          ? (monthItem.forecastHourlyBillRate || 0)
          : (line.linkedResource?.hourlyBillRate || 0);
        const ac = monthItem?.ac || 0;
        const actualHours = monthItem?.actualHours || 0;
        const forecastTotalCost = forecastHours * forecastHourlyCostRate;
        const forecastTotalBill = forecastHours * forecastHourlyBillRate;
        const cv = forecastTotalCost - ac;
        const ForecastHv = forecastHours - actualHours;

        return {
          id: line.id,
          category: line.category,
          description: line.description,
          parentLineId: line.parentLineId || null,
          linkedResource: line.linkedResource || null,
          inv: monthItem?.inv || 0,
          ac,
          gp: monthItem?.gp || 0,
          gpPercentage: monthItem?.gpPercentage || 0,
          itemId: monthItem?.id || null,
          isNew: !monthItem,
          forecastAllocation,
          forecastHours,
          forecastHourlyCostRate,
          forecastHourlyBillRate,
          forecastTotalCost,
          forecastTotalBill,
          cv,
          actualHours,
          ForecastHv,
        };
      });

      // Second pass: re-aggregate parent lines' forecastTotalCost/Bill from their children
      // so we never rely on hours × storedRate (which loses decimals due to rounding).
      const parentIdToChildren = new Map();
      monthLines.forEach(l => {
        if (l.parentLineId) {
          if (!parentIdToChildren.has(l.parentLineId)) parentIdToChildren.set(l.parentLineId, []);
          parentIdToChildren.get(l.parentLineId).push(l);
        }
      });
      const correctedMonthLines = monthLines.map(line => {
        const children = parentIdToChildren.get(line.id);
        if (!children) return line;
        const totalForecastTotalCost = children.reduce((s, c) => s + (c.forecastTotalCost || 0), 0);
        const totalForecastTotalBill = children.reduce((s, c) => s + (c.forecastTotalBill || 0), 0);
        return {
          ...line,
          forecastTotalCost: totalForecastTotalCost,
          forecastTotalBill: totalForecastTotalBill,
          forecastHourlyCostRate: line.forecastHours > 0 ? totalForecastTotalCost / line.forecastHours : 0,
          forecastHourlyBillRate: line.forecastHours > 0 ? totalForecastTotalBill / line.forecastHours : 0,
          cv: totalForecastTotalCost - line.ac,
        };
      });

      return {
        id: cashFlow.id,
        month,
        year: cashFlow.year,
        lines: correctedMonthLines
      };
    });

    const { monthly, yearly } = recalculateMonthlyTotals(monthlyData);
    setData({ monthly, yearly });
    setCashFlow(cashFlow);

    setTimeout(() => {
      if (tableWrapperRef.current) {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const monthColumns = tableWrapperRef.current.querySelectorAll('.month-column');
        if (monthColumns[currentMonth - 1]) {
          const scrollPosition = monthColumns[currentMonth - 1].offsetLeft - 300;
          tableWrapperRef.current.scrollLeft = scrollPosition;
        }
      }
    }, 100);
  }, [recalculateMonthlyTotals]);

  const updateDebounceRef = useRef(null);

  const handleDataUpdate = useCallback((type, currentLine, month, year, value) => {
    setData(prevData => {
      const updatedMonthly = prevData.monthly.map(monthData => {
        if (monthData.month !== month) return monthData;

        // First pass: update the changed sub-line
        let updatedLines = monthData.lines.map(line => {
          if (line.id !== currentLine.id) return line;

          const newInv = type === 'inv' ? value : line.inv;
          const newAc = type === 'ac' ? value : line.ac;
          const newForecastAllocation = type === 'forecastAllocation' ? value : line.forecastAllocation;
          const newForecastHours = type === 'forecastHours' ? value : line.forecastHours;
          const newForecastHourlyCostRate = type === 'forecastHourlyCostRate' ? value : line.forecastHourlyCostRate;
          const newForecastHourlyBillRate = type === 'forecastHourlyBillRate' ? value : line.forecastHourlyBillRate;
          const newActualHours = type === 'actualHours' ? value : line.actualHours;
          const newForecastTotalCost = newForecastHours * newForecastHourlyCostRate;
          const newForecastTotalBill = newForecastHours * newForecastHourlyBillRate;
          const newCv = newForecastTotalCost - newAc;
          const newGp = newInv - newAc;
          const newGpPercentage = newInv > 0 ? (newGp / newInv) * 100 : 0;
          const newForecastHv = newForecastHours - newActualHours;

          return {
            ...line,
            inv: newInv,
            ac: newAc,
            gp: newGp,
            gpPercentage: newGpPercentage,
            forecastAllocation: newForecastAllocation,
            forecastHours: newForecastHours,
            forecastHourlyCostRate: newForecastHourlyCostRate,
            forecastHourlyBillRate: newForecastHourlyBillRate,
            forecastTotalCost: newForecastTotalCost,
            forecastTotalBill: newForecastTotalBill,
            cv: newCv,
            actualHours: newActualHours,
            ForecastHv: newForecastHv,
          };
        });

        // Second pass: if the changed line is a sub-line, re-aggregate its parent
        if (currentLine.parentLineId) {
          const siblingLines = updatedLines.filter(l => l.parentLineId === currentLine.parentLineId);
          updatedLines = updatedLines.map(line => {
            if (line.id !== currentLine.parentLineId) return line;
            const totalAc = siblingLines.reduce((s, l) => s + (l.ac || 0), 0);
            const totalForecastAllocation = siblingLines.reduce((s, l) => s + (l.forecastAllocation || 0), 0);
            const totalForecastHours = siblingLines.reduce((s, l) => s + (l.forecastHours || 0), 0);
            const totalActualHours = siblingLines.reduce((s, l) => s + (l.actualHours || 0), 0);
            const totalForecastTotalCost = siblingLines.reduce((s, l) => s + (l.forecastTotalCost || 0), 0);
            const totalForecastTotalBill = siblingLines.reduce((s, l) => s + (l.forecastTotalBill || 0), 0);
            const avgForecastHourlyCostRate = totalForecastHours > 0 ? totalForecastTotalCost / totalForecastHours : 0;
            const avgForecastHourlyBillRate = totalForecastHours > 0 ? totalForecastTotalBill / totalForecastHours : 0;
            const totalCv = totalForecastTotalCost - totalAc;
            const totalForecastHv = totalForecastHours - totalActualHours;
            const parentGp = line.inv - totalAc;
            const parentGpPercentage = line.inv > 0 ? (parentGp / line.inv) * 100 : 0;
            return {
              ...line,
              ac: totalAc,
              gp: parentGp,
              gpPercentage: parentGpPercentage,
              forecastAllocation: totalForecastAllocation,
              forecastHours: totalForecastHours,
              forecastHourlyCostRate: avgForecastHourlyCostRate,
              forecastHourlyBillRate: avgForecastHourlyBillRate,
              forecastTotalCost: totalForecastTotalCost,
              forecastTotalBill: totalForecastTotalBill,
              cv: totalCv,
              actualHours: totalActualHours,
              ForecastHv: totalForecastHv,
            };
          });
        }

        return { ...monthData, lines: updatedLines };
      });

      return recalculateMonthlyTotals(updatedMonthly);
    });

    if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current);

    updateDebounceRef.current = setTimeout(async () => {
      try {
        setData(prevData => {
          const monthData = prevData.monthly.find(m => m.month === month);
          const lineInMonth = monthData?.lines.find(l => l.id === currentLine.id);
          if (!lineInMonth) return prevData;

          const updateData = {
            projectId,
            lineId: currentLine.id,
            lineItemId: lineInMonth.itemId,
            payload: {
              year,
              month,
              ac: lineInMonth.ac,
              actualHours: lineInMonth.actualHours,
              forecastAllocation: lineInMonth.forecastAllocation,
              forecastHours: lineInMonth.forecastHours,
              forecastHourlyCostRate: lineInMonth.forecastHourlyCostRate,
              forecastHourlyBillRate: lineInMonth.forecastHourlyBillRate,
            }
          };

          const apiCall = lineInMonth.itemId
            ? updateProjectCashflowLineItem(updateData)
            : createProjectCashflowLineItem(updateData);

          apiCall.then(response => {
            dispatch(getProjectBudgetSummary({ id: projectId }));
            fetchKpisRef.current?.();
            if (!lineInMonth.itemId && response?.id) {
              setData(prev => {
                const updated = prev.monthly.map(md => {
                  if (md.month !== month) return md;
                  return {
                    ...md,
                    lines: md.lines.map(l =>
                      l.id === currentLine.id
                        ? { ...l, itemId: response.id, isNew: false }
                        : l
                    )
                  };
                });
                return recalculateMonthlyTotals(updated);
              });
            }
          }).catch(err => console.error('Error saving data:', err));

          return prevData;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    }, 1000);
  }, [projectId, recalculateMonthlyTotals, dispatch]);

  const handleForecastChange = useCallback((line, month, year, field, value) => {
    handleDataUpdate(field, line, month, year, value);
  }, [handleDataUpdate]);

  const handleForecastPairChange = useCallback((primaryField, derivedField, currentLine, month, year, primaryValue, derivedValue) => {
    setData(prevData => {
      const updatedMonthly = prevData.monthly.map(monthData => {
        if (monthData.month !== month) return monthData;
        let updatedLines = monthData.lines.map(line => {
          if (line.id !== currentLine.id) return line;
          const newForecastAllocation = primaryField === 'forecastAllocation' ? primaryValue : derivedField === 'forecastAllocation' ? derivedValue : line.forecastAllocation;
          const newForecastHours = primaryField === 'forecastHours' ? primaryValue : derivedField === 'forecastHours' ? derivedValue : line.forecastHours;
          const newForecastTotalCost = newForecastHours * line.forecastHourlyCostRate;
          const newForecastTotalBill = newForecastHours * line.forecastHourlyBillRate;
          const newCv = newForecastTotalCost - line.ac;
          const newForecastHv = newForecastHours - (line.actualHours || 0);
          return {
            ...line,
            forecastAllocation: newForecastAllocation,
            forecastHours: newForecastHours,
            forecastTotalCost: newForecastTotalCost,
            forecastTotalBill: newForecastTotalBill,
            cv: newCv,
            ForecastHv: newForecastHv,
          };
        });
        // Re-aggregate parent if the changed line is a sub-line
        if (currentLine.parentLineId) {
          const siblingLines = updatedLines.filter(l => l.parentLineId === currentLine.parentLineId);
          updatedLines = updatedLines.map(line => {
            if (line.id !== currentLine.parentLineId) return line;
            const totalAc = siblingLines.reduce((s, l) => s + (l.ac || 0), 0);
            const totalForecastAllocation = siblingLines.reduce((s, l) => s + (l.forecastAllocation || 0), 0);
            const totalForecastHours = siblingLines.reduce((s, l) => s + (l.forecastHours || 0), 0);
            const totalActualHours = siblingLines.reduce((s, l) => s + (l.actualHours || 0), 0);
            const totalForecastTotalCost = siblingLines.reduce((s, l) => s + (l.forecastTotalCost || 0), 0);
            const totalForecastTotalBill = siblingLines.reduce((s, l) => s + (l.forecastTotalBill || 0), 0);
            const avgForecastHourlyCostRate = totalForecastHours > 0 ? totalForecastTotalCost / totalForecastHours : 0;
            const avgForecastHourlyBillRate = totalForecastHours > 0 ? totalForecastTotalBill / totalForecastHours : 0;
            const totalCv = totalForecastTotalCost - totalAc;
            const totalForecastHv = totalForecastHours - totalActualHours;
            return {
              ...line,
              ac: totalAc,
              forecastAllocation: totalForecastAllocation,
              forecastHours: totalForecastHours,
              forecastHourlyCostRate: avgForecastHourlyCostRate,
              forecastHourlyBillRate: avgForecastHourlyBillRate,
              forecastTotalCost: totalForecastTotalCost,
              forecastTotalBill: totalForecastTotalBill,
              cv: totalCv,
              actualHours: totalActualHours,
              ForecastHv: totalForecastHv,
            };
          });
        }
        return { ...monthData, lines: updatedLines };
      });
      return recalculateMonthlyTotals(updatedMonthly);
    });

    // Debounced API save — reads latest state
    if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current);
    updateDebounceRef.current = setTimeout(() => {
      setData(prevData => {
        const monthData = prevData.monthly.find(m => m.month === month);
        const lineInMonth = monthData?.lines.find(l => l.id === currentLine.id);
        if (!lineInMonth) return prevData;
        const updateData = {
          projectId,
          lineId: currentLine.id,
          lineItemId: lineInMonth.itemId,
          payload: {
            year, month,
            ac: lineInMonth.ac,
            actualHours: lineInMonth.actualHours,
            forecastAllocation: lineInMonth.forecastAllocation,
            forecastHours: lineInMonth.forecastHours,
            forecastHourlyCostRate: lineInMonth.forecastHourlyCostRate,
            forecastHourlyBillRate: lineInMonth.forecastHourlyBillRate,
          }
        };
        const apiCall = lineInMonth.itemId
          ? updateProjectCashflowLineItem(updateData)
          : createProjectCashflowLineItem(updateData);
        apiCall.then(response => {
          dispatch(getProjectBudgetSummary({ id: projectId }));
          fetchKpisRef.current?.();
          if (!lineInMonth.itemId && response?.id) {
            setData(prev => {
              const updated = prev.monthly.map(md => {
                if (md.month !== month) return md;
                return { ...md, lines: md.lines.map(l => l.id === currentLine.id ? { ...l, itemId: response.id, isNew: false } : l) };
              });
              return recalculateMonthlyTotals(updated);
            });
          }
        }).catch(err => console.error('Error saving data:', err));
        return prevData;
      });
    }, 1000);
  }, [projectId, recalculateMonthlyTotals, dispatch]);

  const handleAddNewLine = useCallback(async (category) => {
    try {
      const createData = {
        projectId,
        payload: {
          category,
          description: ''
        }
      };

      const newLine = await createProjectCashflowLine(createData);

      setData(prevData => {
        const updatedMonthly = prevData.monthly.map(monthData => {
          const newLineData = {
            id: newLine.id,
            category: newLine.category,
            description: newLine.description || '',
            inv: 0,
            ac: 0,
            gp: 0,
            gpPercentage: 0,
            itemId: null,
            isNew: true
          };

          return {
            ...monthData,
            lines: [...monthData.lines, newLineData]
          };
        });

        return recalculateMonthlyTotals(updatedMonthly);
      });
    } catch (error) {
      console.error('Error adding new line:', error);
    }
  }, [projectId, cashFlow?.id, recalculateMonthlyTotals]);

  const handleRemoveLine = useCallback(async (lineId) => {
    try {
      const deleteData = {
        projectId,
        lineId: lineId
      };

      await deleteProjectCashflowLine(deleteData);

      setData(prevData => {
        const updatedMonthly = prevData.monthly.map(monthData => ({
          ...monthData,
          lines: monthData.lines.filter(line => line.id !== lineId)
        }));

        return recalculateMonthlyTotals(updatedMonthly);
      });

      dispatch(
        generalActions.showToast({
          title: "Line removed successfully",
          type: "success",
        })
      );
    } catch (error) {
      console.error('Error removing line:', error);
    }
  }, [projectId, cashFlow?.id, recalculateMonthlyTotals, dispatch]);

  const handleInvAmountChange = useCallback((line, month, year, value) => {
    handleDataUpdate('inv', line, month, year, value);
  }, [handleDataUpdate]);

  const handleAcAmountChange = useCallback((line, month, year, value) => {
    handleDataUpdate('ac', line, month, year, value);
  }, [handleDataUpdate]);

  const handleActualHoursChange = useCallback((line, month, year, value) => {
    handleDataUpdate('actualHours', line, month, year, value);
  }, [handleDataUpdate]);

  const handleDescriptionChange = useCallback(async (currentLine, value) => {
    try {
      const updateData = {
        projectId,
        lineId: currentLine.id,
        payload: {
          description: value,
          category: currentLine.category
        }
      };

      await updateProjectCashflowLine(updateData);

      setData(prevData => {
        const updatedMonthly = prevData.monthly.map(monthData => {
          const updatedLines = monthData.lines.map(line => {
            if (line.id === currentLine.id) {
              return {
                ...line,
                description: value
              };
            }
            return line;
          });

          return {
            ...monthData,
            lines: updatedLines
          };
        });

        return { ...prevData, monthly: updatedMonthly };
      });
    } catch (error) {
      console.error('Error updating description:', error);
    }
  }, [projectId, cashFlow?.id]);

  const updateProjectCashflowLine = (data) => {
    return projectService.updateProjectCashflowLineRequest(data)
      .catch((error) => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong updating line. Please try again later.",
            type: "error",
          })
        );
        throw error;
      });
  };

  const createProjectCashflowLine = (data) => {
    return projectService.createProjectCashflowLineRequest(data)
      .catch((error) => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong creating line. Please try again later.",
            type: "error",
          })
        );
        throw error;
      });
  };

  const deleteProjectCashflowLine = (data) => {
    return projectService.deleteProjectCashflowLineRequest(data)
      .catch((error) => {
        dispatch(
          generalActions.showToast({
            title: error?.data?.message || "Something went wrong deleting line. Please try again later.",
            type: "error",
          })
        );
        throw error;
      });
  };

  const updateProjectCashflowLineItem = (data) => {
    return projectService.updateProjectCashflowLineItemRequest(data)
      .catch((error) => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong updating line item. Please try again later.",
            type: "error",
          })
        );
        throw error;
      });
  };

  const createProjectCashflowLineItem = (data) => {
    return projectService.createProjectCashflowLineItemRequest(data)
      .catch((error) => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong creating line item. Please try again later.",
            type: "error",
          })
        );
        throw error;
      });
  };

  const fetchProjectCashflow = (cashflowYear) => {
    setIsLoading(true);
    projectService.getProjectCashflowRequest(projectId, cashflowYear ?? year)
      .then((data) => {
        handleCashfLowData(data);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchCashflowCustomLayout = () => {
    setIsLoading(true);
    userService.getUserLayoutRequest(layoutName)
      .then((data) => {
        if (data?.columns?.length > 0) {
          const customLayout = columns.map((c) => {
            const custom = data?.columns?.find(
              (col) => col.name === c.name
            );
            return {
              name: c.name,
              isVisible: custom ? custom.isVisible : c.isVisible,
              customName: custom ? custom.customName : "",
              order: custom ? custom.order : c.order
            };
          });
          customLayout.sort((a, b) => a.order - b.order);
          setColumns(customLayout);
        }
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const debouncedSave = useRef(debounce((updatedColumns) => updateCashflowCustomLayout(updatedColumns), 1000));

  const updateCashflowCustomLayout = (columns) => {
    var payload = {
      name: layoutName,
      columns: columns
    }
    userService.putUserLayoutRequest(payload)
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong Updating Layout. Please try again later.",
            type: "error",
          })
        );
      });
  }
  const handleYearChange = (isNext = false) => {
    const newSelectedYear = isNext ? year + 1 : year - 1;
    setYear(newSelectedYear);
    fetchProjectCashflow(newSelectedYear);
  };

  const handleColumnStateChange = useCallback((name) => {
    setColumns((prev) => {
      const updated = prev.map((col) =>
        col.name === name ? { ...col, isVisible: !col.isVisible } : col
      );

      debouncedSave.current(updated);
      return updated;
    });
  }, []);

  const handleColumnLabelChange = useCallback((name, customName) => {
    setColumns((prev) => {
      const updated = prev.map((col) =>
        col.name === name ? { ...col, customName } : col
      );
      debouncedSave.current(updated);
      return updated;
    });
  }, []);

  const handleResetChanges = useCallback(() => {
    setColumns(defaultLayout);
    debouncedSave.current(defaultLayout);
  }, []);

  const [isReadOnly, setIsReadOnly] = useState(true);
  const [kpiView, setKpiView] = useState('ytd');
  const [showCharts, setShowCharts] = useState(false);

  const handleToggleCharts = useCallback(() => {
    const next = !showCharts;
    setShowCharts(next);
    if (next) {
      fetchProjectCashflow(year);
    }
  }, [showCharts, year]);

  useEffect(() => {
    if (projectId) {
      fetchCashflowCustomLayout();
      fetchProjectCashflow();
      fetchFinancialKpis();
    }
  }, [projectId]);

  return (
    <div className="cash-flow-container">
      {isLoading ? (
        <Skeleton height={300} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
      ) : (
        <>
          <div className="cash-flow-header">
            <div className="absolute left-1">
              <ExportButton config={{ parent: 3, parentId: projectId, category: 1, cashflowYear: year }} />
            </div>

            <KpiGaugeSection
              kpis={kpis}
              view={kpiView}
              onViewChange={setKpiView}
              isSyncing={isKpiSyncing}
              settings={regionalSettings}
            />

            {
              getEditPermission("Budget") && (
                <span
                  className="mr-3 cursor-pointer"
                  onClick={() => setIsReadOnly(prev => !prev)}
                >
                  <Tooltip
                    content={isReadOnly ? 'Read-only mode – click to enable editing' : 'Edit mode – click to enable read-only'}
                    relationship="label">
                    {isReadOnly ? <Eye size={20} color='#342c82' /> : <Pencil size={20} color='#342c82' />}
                  </Tooltip>
                </span>
              )
            }

            {
              !showCharts &&
              <div className="header-Columns">
                <CustomPopover
                  icon={
                    <Button
                      btnClass="d_btn d_btn_dualIconXsm"
                      rightIcon={
                        <span className="columnBadge ms-1">
                          {columns.filter((col) => col.isVisible).length}
                        </span>
                      }
                      icon={<ColumnTripleIcon />}
                      label="Select Columns"
                    />
                  }
                  status={isPopoverOpen}
                  onOpenChange={(e, data) => setIsPopoverOpen(data.open)}
                >
                  <div className="d-flex flex-col gap-1">
                    {columns.map((item) => (
                      <CheckboxInputStateSave
                        label={item.name}
                        isColumnConfig={true}
                        customName={item.customName}
                        checked={item.isVisible}
                        onChange={() => handleColumnStateChange(item.name)}
                        onLabelChange={(newLabel) => handleColumnLabelChange(item.name, newLabel)}
                      />
                    ))}
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        label="Reset Columns"
                        btnClass="d_btn d_btn_primary me-10"
                        clickHandler={handleResetChanges}
                      />
                      <Button
                        label="Cancel"
                        btnClass="d_btn d_btn_smallWhite"
                        btnType="secondary"
                        clickHandler={() => setIsPopoverOpen(false)}
                      />
                    </div>
                  </div>
                </CustomPopover>
              </div>
            }

            <button
              className={`cashflow-view-toggle ${showCharts ? 'is-charts' : 'is-table'}`}
              onClick={handleToggleCharts}
              title={showCharts ? "Switch to Table View" : "Switch to Charts View"}
            >
              <span className="toggle-icon toggle-icon--table"><BarChart2 size={14} /></span>
              <span className="toggle-icon toggle-icon--chart"><TableProperties size={14} /></span>
              <span className="toggle-label">{showCharts ? "Table View" : "Charts View"}</span>
            </button>

            <span className="headerArrow" onClick={() => handleYearChange()}>
              <ArrowLeft />
            </span>
            <p className="text-md-400 subHeading-color ms-1">
              {year}
            </p>
            <span className="headerArrow" onClick={() => handleYearChange(true)}>
              <ArrowRight />
            </span>
            <PendingLockSvg
              toolipText="The monthly cash flow period will be locked 10 days after month-end. Please ensure all entries are reviewed and finalized by then."
            />




          </div>
          <CashFlowKPIBar kpis={kpis} view={kpiView} settings={regionalSettings} />
          {showCharts && (
            <CashFlowCharts
              cashFlowData={data.monthly || []}
              yearlyTotals={data.yearly}
              settings={regionalSettings}
            />
          )}

          {!showCharts && (
            <div
              className="cash-flow-table-wrapper"
              ref={tableWrapperRef}
              style={{ maxHeight: `calc(100vh - ${isHeaderCollapsed ? '380' : '574'}px)` }}
            >
              <table className="cash-flow-table">
                <CashFlowHeader
                  cashFlowData={data.monthly || []}
                  columns={columns}
                  startResize={startResize}
                />
                <tbody>
                  {categories.map(category => (
                    <CashFlowRow
                      projectId={projectId}
                      columns={columns}
                      key={category}
                      category={category}
                      cashFlowData={data.monthly || []}
                      settings={regionalSettings}
                      isReadOnly={isReadOnly}
                      onInAmountChange={handleInvAmountChange}
                      onOutAmountChange={handleAcAmountChange}
                      onForecastChange={handleForecastChange}
                      onDescriptionChange={handleDescriptionChange}
                      onAddNewLine={handleAddNewLine}
                      onRemoveLine={handleRemoveLine}
                      onForecastPairChange={handleForecastPairChange}
                      onActualHoursChange={handleActualHoursChange}
                    />
                  ))}
                  <CashFlowSummary
                    columns={columns}
                    cashFlowData={data.monthly || []}
                    yearlyTotals={data.yearly}
                    settings={regionalSettings}
                  />
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CashFlow;